import { useContext, useEffect } from 'react';

import { useRouter } from 'next/router';

import useOptimoveSetLoggedUser from '@/common/analytics/optimove/hooks/useOptimoveSetLoggedUser';
import { extendAthenaUserIdCookie } from '@/common/athena/checkAndSetAthenaCookie';
import ReactIframe from '@/common/components/ReactIframe/ReactIframe';
import { ConfigPublic } from '@/common/config.public';
import { AuthContext } from '@/contexts/auth/AuthContext';

const SESSION_PATH = `elysium-api/user/v1/session`;

/**
 * We had issues with session iframe on Safari. When iframe sent onLoad event, the react code was not loaded yet.
 * This resulted in situation when there was no event listener for message event and page did not load user context.
 * To fix this, BFW sends session response with HTML script content that sets parent.elysiumSession variable and sends message.
 * If react is loaded -> then it will handle the message and init auth context.
 * Id react is not loaded -> then it will see the elysiumSession variable and init auth context even without receiving message event.
 */

const SessionIframe = () => {
  const { cookieLogin, errorLogin } = useContext(AuthContext);
  const { query } = useRouter();
  const ctrc = query.ctrc as string | undefined;
  useOptimoveSetLoggedUser();

  /**
   * Create a session url with optional country code param.
   * @param countryCode Value taken from url query ?ctrc=US (or PL, SE, GB)
   * @returns url for session creation.
   */
  const createSessionUrl = (countryCode: string | undefined): string => {
    const countryCodeParam = countryCode ? `?ctrc=${countryCode}` : '';
    return `${ConfigPublic.AppUrl}/${SESSION_PATH}${countryCodeParam}`;
  };

  const handleIframeLoad = () => {
    if (window.elysiumSession) {
      cookieLogin();
      return;
    }
    errorLogin();
  };

  useEffect(() => {
    extendAthenaUserIdCookie();
  }, []);

  return (
    <ReactIframe
      id="session-iframe"
      loading="lazy"
      name="session-frame"
      onLoad={handleIframeLoad}
      src={createSessionUrl(ctrc)}
      style={{ position: 'absolute', width: 5, height: 5, border: 0 }}
      suppressHydrationWarning
    />
  );
};

export default SessionIframe;
